/**
 * ScanMailButton — "Scan" for a vendor master database (Crossroads, Hilti).
 * Pre-scan dialog (search word/email + date range) -> scans EVERY connected
 * mailbox across the WHOLE range (server-side search, not the cached 200),
 * pulls PDF attachments, LLM-detects vendor invoices/statements, then shows
 * a results dialog. Duplicates (same invoice #) must be Skip or Replace with
 * a side-by-side comparison; nothing is written until Confirm.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Database, FileText, Loader2, MailSearch, X } from "lucide-react";
import api from "@/lib/api";

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Overlay = ({ children, onClose, wide }) => (
  <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
    <div
      className={`max-h-[88vh] w-full ${wide ? "max-w-3xl" : "max-w-md"} overflow-y-auto rounded-xl border border-white/15 bg-[#0d0d0d] p-5 shadow-2xl`}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  </div>
);

const SummaryRows = ({ s }) => {
  if (!s) return null;
  const rows = s.doc_type === "expense"
    ? [["Vendor", s.vendor || "—"], ["Category", s.category || "—"], ["Date", s.date || "—"], ["Amount", fmtMoney(s.amount)],
       ["Invoice / receipt #", s.invoice_number || "—"], ["Account", s.account_number || "—"], ["Renews", s.renewal_date || "—"],
       ["What", s.description || "—"], ["Source", s.source || "—"]]
    : s.doc_type === "invoice"
    ? [["Invoice #", s.invoice_number], ["Date", s.invoice_date || "—"], ["Due", s.due_date || "—"],
       ["Amount", fmtMoney(s.total_due)], ["P.O.", s.po_number || "—"], ["Items", s.items ?? "—"], ["Source", s.source || "—"]]
    : [["Account", s.account_number || "—"], ["Stmt date", s.statement_date || "—"],
       ["Amount", fmtMoney(s.total_amount_due)], ["Invoices listed", s.rows ?? "—"], ["Source", s.source || "—"]];
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-2 text-[11px]">
          <dt className="text-white/40">{k}</dt>
          <dd className="truncate text-right font-medium text-white/85" title={String(v)}>{v}</dd>
        </div>
      ))}
    </dl>
  );
};

/**
 * mode "email" (default): scan connected mailboxes for a date range.
 * mode "qb": pull QuickBooks Purchases + Bills for a date range instead.
 * Both run as server-side jobs under `basePath` (default /master/{vendor}) and
 * share the same progress / duplicate-compare / skip-replace / commit flow.
 */
export default function ScanMailButton({ vendor, title, onDone, basePath, mode = "email" }) {
  const base = basePath || `/master/${vendor}`;
  const isQb = mode === "qb";
  const tid = isQb ? `${vendor}-qb` : `${vendor}-scan`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [running, setRunning] = useState(null);   // live job status while scanning
  const [results, setResults] = useState(null);   // finished job
  const [decisions, setDecisions] = useState({});
  const [compare, setCompare] = useState(null);   // cand_id with comparison open
  const [committing, setCommitting] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const beginPolling = (jobId) => {
    clearInterval(pollRef.current);
    setRunning((prev) => ({ ...(prev || {}), job_id: jobId }));
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`${base}/scan/${jobId}`);
        if (r.data.status === "done" || r.data.status === "error" || r.data.status === "canceled") {
          clearInterval(pollRef.current);
          setRunning(null);
          if (r.data.status === "canceled") return;
          if (r.data.status === "error") {
            toast.error(`${isQb ? "QuickBooks import" : "Scan"} failed: ${r.data.error || "unknown error"}`);
            return;
          }
          setResults(r.data);
          const d = {};
          (r.data.candidates || []).forEach((c) => { d[c.cand_id] = c.dup ? "skip" : "import"; });
          setDecisions(d);
          if (!(r.data.candidates || []).length) toast.info(isQb ? `QuickBooks has no ${title} expenses in that range` : `No ${title} documents found in that range`);
        } else {
          setRunning(r.data);
        }
      } catch { /* keep polling */ }
    }, 2000);
  };

  // Resume: the scan runs on the server, so if the app reloaded mid-scan
  // (phone call, screen lock) reopen the progress window or the results.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`${base}/scan/latest`);
        const job = data?.job;
        if (!job) return;
        if (job.status === "done") {
          setResults(job);
          const d = {};
          (job.candidates || []).forEach((c) => { d[c.cand_id] = c.dup ? "skip" : "import"; });
          setDecisions(d);
        } else {
          setRunning(job);
          beginPolling(job.job_id);
        }
      } catch { /* no scan to resume */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor]);

  const startScan = async () => {
    if (!from || !to) {
      toast.error("Pick a from and to date first");
      return;
    }
    try {
      const { data } = isQb
        ? await api.post(`${base}/qb-import/start`, { date_from: from, date_to: to })
        : await api.post(`${base}/scan/start`, { query: query.trim(), date_from: from, date_to: to });
      setOpen(false);
      setRunning({ status: "running", accounts_searched: 0, accounts_total: 0, attachments_found: 0, parsed: 0, found: 0 });
      beginPolling(data.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the scan");
    }
  };

  const cancelPolling = () => { clearInterval(pollRef.current); setRunning(null); };
  // Cancel = stop the server job too (Close alone leaves it running in the background)
  const cancelJob = async () => {
    const jobId = running?.job_id;
    clearInterval(pollRef.current);
    setRunning(null);
    if (!jobId) return;
    try {
      await api.post(`/master/scan-jobs/${jobId}/cancel`);
      toast.info(isQb ? "QuickBooks import cancelled" : "Scan cancelled");
    } catch { toast.error("Could not cancel — it will finish on its own"); }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post(`${base}/scan/${results.job_id}/commit`, { decisions });
      toast.success(`Scan import done — ${data.imported} added, ${data.replaced} replaced, ${data.skipped} skipped`);
      setResults(null);
      await onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setCommitting(false);
    }
  };

  const discardAll = async () => {
    setCommitting(true);
    try {
      const all = {};
      cands.forEach((c) => { all[c.cand_id] = "skip"; });
      await api.post(`${base}/scan/${results.job_id}/commit`, { decisions: all });
      setResults(null);
      toast.info("Scan results discarded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not discard");
    } finally {
      setCommitting(false);
    }
  };

  const cands = results?.candidates || [];
  const counts = cands.reduce((a, c) => {
    const d = decisions[c.cand_id];
    a[d] = (a[d] || 0) + 1;
    return a;
  }, {});

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`${tid}-btn`}
        className="flex items-center gap-2 rounded-md border border-[#1a3fd1]/60 bg-[#1a3fd1]/15 px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-widest text-[#8fa4f0] hover:bg-[#1a3fd1]/30"
      >
        {isQb ? <><Database className="h-4 w-4" /> QB import</> : <><MailSearch className="h-4 w-4" /> Scan email</>}
      </button>

      {/* -------- pre-scan dialog -------- */}
      {open && (
        <Overlay onClose={() => setOpen(false)}>
          <div className="mb-4 flex items-start justify-between">
            <div>
              <h3 className="text-sm font-bold text-white" data-testid={`${tid}-dialog`}>{isQb ? `Import ${title} expenses from QuickBooks` : `Scan email for ${title} documents`}</h3>
              <p className="mt-1 text-xs text-white/45">
                {isQb
                  ? `Pulls every QuickBooks purchase and bill dated in the range${vendor === "overhead" ? " (Crossroads and Hilti go to their own databases)" : ` from ${title}`}. Anything already in the database is flagged as a duplicate for you to skip or replace.`
                  : vendor === "overhead"
                    ? "Reads every email in the range — PDF attachments AND text-only receipts in the body — and keeps the ones that look like a bill, invoice or receipt. Crossroads and Hilti mail is skipped (they have their own databases)."
                    : `Searches every connected mailbox across the whole date range for PDF attachments that look like ${title} invoices or account statements.`}
              </p>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close" className="rounded p-1 text-white/40 hover:text-white"><X className="h-4 w-4" /></button>
          </div>
          {!isQb && <><label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/40">Search word or email address (optional)</label>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`e.g. invoice or orders@${vendor === "hilti" ? "hilti" : "ccihq"}.com`}
            data-testid={`${tid}-query`}
            className="mb-4 w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/25 focus:border-[#4f6fe8] focus:outline-none"
          /></>}
          <div className="mb-5 grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/40">From</label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                data-testid={`${tid}-from`}
                className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm text-white [color-scheme:dark] focus:border-[#4f6fe8] focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/40">To</label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                data-testid={`${tid}-to`}
                className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm text-white [color-scheme:dark] focus:border-[#4f6fe8] focus:outline-none" />
            </div>
          </div>
          <button
            type="button"
            onClick={startScan}
            data-testid={`${tid}-start`}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-[#1a3fd1] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1633a3]"
          >
            {isQb ? <><Database className="h-4 w-4" /> Pull from QuickBooks</> : <><MailSearch className="h-4 w-4" /> Start scan</>}
          </button>
        </Overlay>
      )}

      {/* -------- progress -------- */}
      {running && (
        <Overlay onClose={cancelPolling}>
          <div className="flex flex-col items-center py-6 text-center" data-testid={`${tid}-progress`}>
            <Loader2 className="mb-3 h-8 w-8 animate-spin text-[#4f6fe8]" />
            <p className="text-sm font-semibold text-white">
              {isQb
                ? (running.status === "parsing" ? "Checking for duplicates…" : "Pulling expenses from QuickBooks…")
                : (running.status === "parsing" ? "Reading attachments…" : "Searching mailboxes…")}
            </p>
            <p className="mt-2 text-xs text-white/50">
              {isQb ? (
                <>QuickBooks expenses pulled {running.attachments_found || 0}
                  {running.status === "parsing" && <>{" · "}Checked {running.parsed || 0}</>}
                  {" · "}<b className="text-[#8fa4f0]">New for {title} {running.found || 0}</b></>
              ) : (
                <>Accounts {running.accounts_searched || 0}/{running.accounts_total || "…"}
                  {" · "}Attachments found {running.attachments_found || 0}
                  {" · "}Read {running.parsed || 0}
                  {" · "}<b className="text-[#8fa4f0]">{title} documents {running.found || 0}</b></>
              )}
            </p>
            <p className="mt-3 max-w-xs text-[11px] text-white/35">
              {isQb ? "Nothing is scanned or read by AI here — this only copies expenses that already exist in QuickBooks. " : "Large date ranges can take a few minutes — "}
              Close keeps it running in the background; Cancel stops it.
            </p>
            <div className="mt-4 flex gap-2">
              <button onClick={cancelPolling} data-testid={`${tid}-progress-close`} className="rounded-md border border-white/15 px-4 py-1.5 text-xs text-white/60 hover:text-white">Close</button>
              <button onClick={cancelJob} data-testid={`${tid}-progress-cancel`} className="rounded-md border border-red-400/40 bg-red-500/10 px-4 py-1.5 text-xs font-semibold text-red-200 hover:bg-red-500/20">
                {isQb ? "Cancel import" : "Cancel scan"}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* -------- results -------- */}
      {results && (
        <Overlay wide onClose={() => setResults(null)}>
          <div className="mb-4 flex items-start justify-between">
            <div>
              <h3 className="text-sm font-bold text-white" data-testid={`${tid}-results`}>
                {isQb
                  ? `QuickBooks has ${cands.length} ${title} expense${cands.length === 1 ? "" : "s"} to import`
                  : `Scan found ${cands.length} ${title} document${cands.length === 1 ? "" : "s"}`}
              </h3>
              <p className="mt-1 text-xs text-white/45">
                {results.date_from} → {results.date_to}{results.query && !isQb ? ` · “${results.query}”` : ""}
                {" · "}{results.attachments_found || 0} {isQb ? "QuickBooks expenses checked" : "attachments checked"}
              </p>
            </div>
            <button onClick={() => setResults(null)} aria-label="Close" className="rounded p-1 text-white/40 hover:text-white"><X className="h-4 w-4" /></button>
          </div>

          {cands.length === 0 ? (
            <p className="py-8 text-center text-xs text-white/40">{isQb ? "QuickBooks has no expenses in that range that aren't already here." : "Nothing matched. Try a wider date range or a different search word."}</p>
          ) : (
            <div className="space-y-3">
              {cands.map((c) => {
                const dup = c.dup;
                const decision = decisions[c.cand_id];
                const s = { doc_type: c.doc_type, vendor: c.parsed?.vendor,
                  invoice_number: c.parsed?.invoice_number, invoice_date: c.parsed?.invoice_date,
                  due_date: c.parsed?.due_date, total_due: c.parsed?.total_due,
                  po_number: c.parsed?.po_number, items: (c.parsed?.items || []).length,
                  account_number: c.parsed?.account_number, statement_date: c.parsed?.statement_date,
                  total_amount_due: c.parsed?.total_amount_due, rows: (c.parsed?.rows || []).length,
                  category: c.parsed?.category, date: c.parsed?.date, amount: c.parsed?.amount,
                  description: c.parsed?.description, renewal_date: c.parsed?.renewal_date,
                  source: c.filename };
                return (
                  <div key={c.cand_id} data-testid={`${tid}-cand-${c.cand_id}`}
                    className={`rounded-lg border p-3 ${dup ? "border-amber-500/40 bg-amber-500/[0.06]" : "border-white/10 bg-white/[0.03]"}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <FileText className="h-4 w-4 shrink-0 text-white/40" />
                      <span className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-white/60">
                        {c.doc_type}
                      </span>
                      <span className="text-xs font-semibold text-white">
                        {c.doc_type === "expense" ? `${c.parsed?.vendor || "?"} · ${fmtMoney(c.parsed?.amount)}` : c.doc_type === "invoice" ? `Invoice ${c.parsed?.invoice_number || "?"}` : `Statement ${c.parsed?.statement_date || ""}`}
                      </span>
                      <span className="text-[11px] text-white/40">{c.filename}</span>
                      {dup && (
                        <span className="rounded bg-amber-500/20 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-amber-300">
                          Duplicate — already {dup.where === "this scan" ? "found in this scan" : "in the database"}
                        </span>
                      )}
                      <span className="ml-auto flex items-center gap-2">
                        {dup ? (
                          <>
                            <button type="button" onClick={() => setCompare(compare === c.cand_id ? null : c.cand_id)}
                              data-testid={`${tid}-compare-${c.cand_id}`}
                              className="rounded border border-white/15 px-2 py-1 text-[10px] font-semibold text-white/70 hover:text-white">
                              {compare === c.cand_id ? "Hide compare" : "Compare"}
                            </button>
                            <label className={`flex cursor-pointer items-center gap-1 rounded px-2 py-1 text-[10px] font-semibold ${decision === "skip" ? "bg-white/15 text-white" : "text-white/45"}`}>
                              <input type="radio" className="hidden" checked={decision === "skip"}
                                onChange={() => setDecisions((d) => ({ ...d, [c.cand_id]: "skip" }))}
                                data-testid={`${tid}-skip-${c.cand_id}`} /> Skip
                            </label>
                            <label className={`flex cursor-pointer items-center gap-1 rounded px-2 py-1 text-[10px] font-semibold ${decision === "replace" ? "bg-amber-500/25 text-amber-200" : "text-white/45"}`}>
                              <input type="radio" className="hidden" checked={decision === "replace"}
                                onChange={() => setDecisions((d) => ({ ...d, [c.cand_id]: "replace" }))}
                                data-testid={`${tid}-replace-${c.cand_id}`} /> Replace
                            </label>
                          </>
                        ) : (
                          <label className="flex cursor-pointer items-center gap-1.5 text-[11px] font-semibold text-white/75">
                            <input type="checkbox" checked={decision === "import"}
                              onChange={(e) => setDecisions((d) => ({ ...d, [c.cand_id]: e.target.checked ? "import" : "skip" }))}
                              data-testid={`${tid}-import-${c.cand_id}`}
                              className="h-3.5 w-3.5 accent-[#1a3fd1]" /> Import
                          </label>
                        )}
                      </span>
                    </div>
                    <p className="mt-1 truncate text-[11px] text-white/40">
                      {isQb ? `QuickBooks · ${c.email_date}${c.qbo_id ? ` · ${c.qbo_id.replace(":", " #")}` : ""}` : `${c.email_from} · ${c.email_subject || "(no subject)"} · ${c.email_date} · ${c.account}`}
                    </p>
                    {dup && compare === c.cand_id && (
                      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid={`${tid}-compare-panel-${c.cand_id}`}>
                        <div className="rounded-md border border-white/10 bg-black/20 p-3">
                          <p className="mb-2 font-mono text-[9px] uppercase tracking-widest text-white/40">
                            {dup.where === "this scan" ? "Earlier in this scan" : "Already saved"}
                          </p>
                          <SummaryRows s={dup} />
                        </div>
                        <div className="rounded-md border border-[#1a3fd1]/40 bg-[#1a3fd1]/10 p-3">
                          <p className="mb-2 font-mono text-[9px] uppercase tracking-widest text-[#8fa4f0]">{isQb ? "In QuickBooks" : "Found in email"}</p>
                          <SummaryRows s={s} />
                        </div>
                      </div>
                    )}
                    {!dup && (
                      <div className="mt-2">
                        <SummaryRows s={s} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {cands.length > 0 && (
            <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-4">
              <p className="text-[11px] text-white/45">
                {(counts.import || 0) + (counts.replace || 0)} to save
                {counts.replace ? ` (${counts.replace} replacing existing)` : ""} · {counts.skip || 0} skipped
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={discardAll}
                  disabled={committing}
                  data-testid={`${tid}-discard`}
                  className="rounded-md border border-white/15 px-4 py-2 text-xs font-semibold text-white/60 hover:text-white disabled:opacity-40"
                >
                  Discard all
                </button>
                <button
                  type="button"
                  onClick={commit}
                  disabled={committing || !(counts.import || counts.replace)}
                  data-testid={`${tid}-commit`}
                  className="flex items-center gap-2 rounded-md bg-[#1a3fd1] px-5 py-2 text-sm font-semibold text-white hover:bg-[#1633a3] disabled:opacity-40"
                >
                  {committing && <Loader2 className="h-4 w-4 animate-spin" />}
                  {committing ? "Importing…" : "Confirm import"}
                </button>
              </div>
            </div>
          )}
        </Overlay>
      )}
    </>
  );
}
